/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { useJsApiLoader, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';
import { toast } from 'react-toastify';

// Configure axios for the driver location map
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
axios.defaults.baseURL = baseUrl;
axios.defaults.withCredentials = true;

// Add authorization header
axios.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Map configuration
const mapContainerStyle = {
  width: '100%',
  height: '600px'
};

// Google Maps libraries - static array to prevent reloading
const GOOGLE_MAPS_LIBRARIES = ['places'];

const defaultCenter = {
  lat: 50.7184, // Exeter, Devon
  lng: -3.5339
};

const mapOptions = {
  zoomControl: true,
  streetViewControl: true,
  fullscreenControl: true,
  mapTypeControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    }
  ]
};

// Check if driver is available (working status and recent location data)
const isDriverAvailable = (driver) => {
  // Primary check: Is the driver working (Go button pressed)?
  if (driver.status && driver.status.isWorking === false) {
    return false;
  }
  
  // Secondary check: Does the driver have recent location data?
  if (!driver.location || !driver.location.lastUpdated) {
    return false;
  }
  
  // Check if location was updated within the last 60 minutes
  const lastUpdated = new Date(driver.location.lastUpdated);
  const now = new Date();
  const timeDiff = now - lastUpdated;
  const sixtyMinutes = 60 * 60 * 1000; // 60 minutes in milliseconds
  
  const hasRecentLocation = timeDiff <= sixtyMinutes;
  
  if (!hasRecentLocation) {
    return false;
  }
  
  // Driver is working AND has recent location data
  return true;
};

// Custom marker icon for drivers
const createDriverIcon = (driver, isActive = true) => {
  const size = 35; // Fixed size regardless of zoom
  const opacity = isActive ? 1.0 : 0.7;
  
  // If driver has a logo
  if (driver.logo) {
    if (isActive) {
      // Active driver with logo - use logo directly
      return {
        url: driver.logo,
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2)
      };
    } else {
      // Inactive driver with logo - create composite with red diagonal line
      const svgContent = `
        <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <clipPath id="logoClip">
              <circle cx="17.5" cy="17.5" r="14"/>
            </clipPath>
          </defs>
          <circle cx="17.5" cy="17.5" r="14" fill="#dc3545" stroke="#fff" stroke-width="2" opacity="${opacity}"/>
          <image href="${driver.logo}" x="3.5" y="3.5" width="28" height="28" clip-path="url(#logoClip)" opacity="0.3"/>
          <line x1="7" y1="7" x2="28" y2="28" stroke="#fff" stroke-width="2.5" opacity="0.9"/>
        </svg>
      `;
      
      return {
        url: 'data:image/svg+xml;base64,' + btoa(svgContent),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2)
      };
    }
  }
  
  // No logo - create a consistent 35x35 icon with availability indicator
  const svgContent = `
    <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="17.5" cy="17.5" r="14" fill="${isActive ? '#28a745' : '#dc3545'}" stroke="#fff" stroke-width="2" opacity="${opacity}"/>
      ${!isActive ? '<line x1="7" y1="7" x2="28" y2="28" stroke="#fff" stroke-width="2.5" opacity="0.9"/>' : ''}
    </svg>
  `;
  
  return {
    url: 'data:image/svg+xml;base64,' + btoa(svgContent),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2)
  };
};

// Driver info component
const DriverInfo = ({ driver, onClose }) => (
  <div className="driver-info-popup">
    <div className="d-flex align-items-center mb-2">
      {driver.logo && (
        <img 
          src={driver.logo} 
          alt={`${driver.name} logo`}
          className="me-2"
          style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
        />
      )}
      <div>
        <h6 className="mb-0">{driver.name}</h6>
        <small className="text-muted">{driver.companyName}</small>
      </div>
    </div>
    
    <div className="mb-2">
      <strong>Location:</strong>
      <br />
      <small className="text-muted">
        {driver.location.address || `${driver.location.latitude.toFixed(6)}, ${driver.location.longitude.toFixed(6)}`}
      </small>
    </div>
    
    <div className="mb-2">
      <strong>Status:</strong>
      <div className="mt-1">
        <Badge bg={driver.status.profileCompleted ? 'success' : 'warning'} className="me-1">
          Profile {driver.status.profileCompleted ? 'Complete' : 'Incomplete'}
        </Badge>
        <Badge bg={driver.status.licensingApproved ? 'success' : 'danger'}>
          License {driver.status.licensingApproved ? 'Approved' : 'Pending'}
        </Badge>
      </div>
    </div>
    
    {driver.location.accuracy && (
      <div className="mb-2">
        <strong>Accuracy:</strong> {Math.round(driver.location.accuracy)}m
      </div>
    )}
    
    {driver.location.lastUpdated && (
      <div className="mb-2">
        <strong>Last Updated:</strong>
        <br />
        <small className="text-muted">
          {new Date(driver.location.lastUpdated).toLocaleString()}
        </small>
      </div>
    )}
    
    <div className="d-flex justify-content-between">
      <Button variant="outline-primary" size="sm" onClick={onClose}>
        Close
      </Button>
      <Button 
        variant="outline-info" 
        size="sm"
        onClick={() => {
          const mapsUrl = `https://www.google.com/maps?q=${driver.location.latitude},${driver.location.longitude}`;
          window.open(mapsUrl, '_blank');
        }}
      >
        Open in Maps
      </Button>
    </div>
  </div>
);

export default function DriverLocationMap() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);
  const [showAllDrivers, setShowAllDrivers] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive

  // Google Maps API loader
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Load driver locations
  const loadDriverLocations = useCallback(async (isInitialLoad = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/admin/public/drivers/locations');
      
      if (response.data.success) {
        setDrivers(response.data.drivers);
        
        // Only update map center on initial load, not on auto-refresh
        if (isInitialLoad && response.data.drivers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          response.data.drivers.forEach(driver => {
            bounds.extend({
              lat: driver.location.latitude,
              lng: driver.location.longitude
            });
          });
          
          const center = bounds.getCenter();
          setMapCenter({
            lat: center.lat(),
            lng: center.lng()
          });
        }
        
        if (isInitialLoad) {
          toast.success(`Loaded ${response.data.drivers.length} driver locations`);
        }
      } else {
        throw new Error(response.data.error || 'Failed to load driver locations');
      }
    } catch (err) {
      console.error('Error loading driver locations:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load driver locations');
      if (isInitialLoad) {
        toast.error('Failed to load driver locations');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    if (isLoaded) {
      loadDriverLocations(true); // Initial load
    }
  }, [isLoaded, loadDriverLocations]);

  // Auto-refresh driver locations every 30 seconds
  useEffect(() => {
    if (!isLoaded) return;

    const refreshInterval = setInterval(() => {
      loadDriverLocations(false); // Auto-refresh, don't change map center
    }, 30000); // 30 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [isLoaded, loadDriverLocations]);

  // Filter drivers based on status - show working drivers or those without status set
  const filteredDrivers = drivers.filter(driver => {
    // During transition period, show ALL drivers regardless of working status
    // This allows drivers to be visible while they learn the new system
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return driver.status.profileCompleted && driver.status.licensingApproved;
    if (filterStatus === 'inactive') return !driver.status.profileCompleted || !driver.status.licensingApproved;
    return true;
  });

  // Handle marker click
  const handleMarkerClick = (driver) => {
    setSelectedDriver(driver);
  };

  // Handle info window close
  const handleInfoWindowClose = () => {
    setSelectedDriver(null);
  };

  // Handle map click
  const handleMapClick = () => {
    setSelectedDriver(null);
  };

  // Fit map to show all drivers
  const fitMapToDrivers = () => {
    if (filteredDrivers.length === 0) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    filteredDrivers.forEach(driver => {
      bounds.extend({
        lat: driver.location.latitude,
        lng: driver.location.longitude
      });
    });
    
    // This would need to be called on the map instance
    // For now, we'll update the center and zoom
    const center = bounds.getCenter();
    setMapCenter({
      lat: center.lat(),
      lng: center.lng()
    });
    setMapZoom(12);
  };

  if (loadError) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Google Maps Error</Alert.Heading>
        <p>Failed to load Google Maps. Please check your API key configuration.</p>
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading Google Maps...</span>
        </Spinner>
        <p className="mt-2">Loading Google Maps...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <Card className="mb-3">
        <Card.Body>
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="d-flex gap-2 mb-2">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => loadDriverLocations(false)}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={fitMapToDrivers}
                  disabled={filteredDrivers.length === 0}
                >
                  Fit to Drivers
                </Button>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex gap-2 justify-content-md-end">
                <select 
                  className="form-select form-select-sm" 
                  style={{ width: 'auto' }}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Drivers ({drivers.length})</option>
                  <option value="active">Active Only ({drivers.filter(d => d.status.profileCompleted && d.status.licensingApproved).length})</option>
                  <option value="inactive">Inactive Only ({drivers.filter(d => !d.status.profileCompleted || !d.status.licensingApproved).length})</option>
                </select>
              </div>
            </div>
          </div>
          
          {error && (
            <Alert variant="danger" className="mt-2">
              {error}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Map */}
      <Card>
        <Card.Body className="p-0">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={mapZoom}
            options={mapOptions}
            onClick={handleMapClick}
            onCenterChanged={() => {
              // Update center when user moves map
              if (window.google && window.google.maps) {
                const map = window.google.maps.Map;
                if (map && map.getCenter) {
                  const center = map.getCenter();
                  if (center) {
                    setMapCenter({
                      lat: center.lat(),
                      lng: center.lng()
                    });
                  }
                }
              }
            }}
            onZoomChanged={() => {
              // Update zoom when user zooms map
              if (window.google && window.google.maps) {
                const map = window.google.maps.Map;
                if (map && map.getZoom) {
                  setMapZoom(map.getZoom());
                }
              }
            }}
          >
            {filteredDrivers
              .sort((a, b) => {
                // Sort drivers so active drivers (isAvailable: true) appear last (on top)
                const aActive = isDriverAvailable(a);
                const bActive = isDriverAvailable(b);
                if (aActive && !bActive) return 1; // a goes after b
                if (!aActive && bActive) return -1; // a goes before b
                return 0; // maintain original order
              })
              .map((driver) => {
                const isAvailable = isDriverAvailable(driver);
                
                return (
                  <Marker
                    key={driver.id}
                    position={{
                      lat: driver.location.latitude,
                      lng: driver.location.longitude
                    }}
                    icon={createDriverIcon(
                      driver, 
                      isAvailable
                    )}
                    onClick={() => handleMarkerClick(driver)}
                  />
                );
              })}
            
            {selectedDriver && (
              <InfoWindow
                position={{
                  lat: selectedDriver.location.latitude,
                  lng: selectedDriver.location.longitude
                }}
                onCloseClick={handleInfoWindowClose}
              >
                <DriverInfo 
                  driver={selectedDriver} 
                  onClose={handleInfoWindowClose}
                />
              </InfoWindow>
            )}
          </GoogleMap>
        </Card.Body>
      </Card>

      {/* Driver List */}
      {filteredDrivers.length > 0 && (
        <Card className="mt-3">
          <Card.Header>
            <h6 className="mb-0">Driver Locations ({filteredDrivers.length})</h6>
          </Card.Header>
          <Card.Body>
            <div className="row">
              {filteredDrivers.map((driver) => (
                <div key={driver.id} className="col-md-6 col-lg-4 mb-3">
                  <div 
                    className="card h-100 cursor-pointer"
                    onClick={() => {
                      setSelectedDriver(driver);
                      setMapCenter({
                        lat: driver.location.latitude,
                        lng: driver.location.longitude
                      });
                      setMapZoom(15);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-2">
                        {driver.logo && (
                          <img 
                            src={driver.logo} 
                            alt={`${driver.name} logo`}
                            className="me-2"
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        )}
                        <div>
                          <h6 className="mb-0">{driver.name}</h6>
                          <small className="text-muted">{driver.companyName}</small>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted">
                          {driver.location.address || `${driver.location.latitude.toFixed(4)}, ${driver.location.longitude.toFixed(4)}`}
                        </small>
                      </div>
                      
                      <div className="d-flex gap-1">
                        <Badge bg={driver.status.profileCompleted ? 'success' : 'warning'} className="small">
                          Profile
                        </Badge>
                        <Badge bg={driver.status.licensingApproved ? 'success' : 'danger'} className="small">
                          License
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
